import React, { useState, useEffect, useRef, useMemo } from "react";
import { 
  TextField, 
  Autocomplete, 
  Box, Divider, Typography, IconButton, ListItem,
  ListItemText,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  fetchForInput, 
  validateDecimal,
  handleError,
  resolveDateFormattingLocale,
  bcp47ToDayjsLocale,
  getDayjsDateTimeFormatForLocale,
  resolveHour12Preference,
} from "../../utils/lib";
import axios from "axios";
import { useSnackbar } from "../../providers/SnackBarProvider";
import { useAppBar } from "../../providers/AppBarProvider";
import EditPage from "./EditPage";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import "dayjs/locale/es";
import "dayjs/locale/pt";
import "dayjs/locale/en-gb";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import CloseIcon from "@mui/icons-material/Close";
import InventoryIcon from '@mui/icons-material/Inventory';
import PetsIcon from "@mui/icons-material/Pets";
import SearchIcon from "@mui/icons-material/Search";
import useNavigationHelperStore from '../../store/navigationHelperStore';
import { useTranslation } from 'react-i18next';
import { useConfig } from "../../providers/ConfigProvider";

const EditRecipe = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { updateTitle, toggleFab } = useAppBar();
  const { config, isLoadingConfig } = useConfig();
  const [ isLoading, setIsLoading ] = useState(false);

  const dateFormattingLocale = useMemo(() => resolveDateFormattingLocale(config), [config]);
  const useHour12 = useMemo(
    () => resolveHour12Preference(config, dateFormattingLocale),
    [config, dateFormattingLocale]
  );
  const dateTimePickerFormat = useMemo(
    () => getDayjsDateTimeFormatForLocale(dateFormattingLocale, useHour12),
    [dateFormattingLocale, useHour12]
  );
  const dayjsPickerLocale = useMemo(
    () => bcp47ToDayjsLocale(dateFormattingLocale),
    [dateFormattingLocale]
  );
  const { setNavigation, from, state: navState, resetNavigation } = useNavigationHelperStore();
  
  const { updateRecipe, pet } = location.state || {};

  const [recipe, setRecipe] = useState(() => {
    if (updateRecipe) {
      let dateValue = updateRecipe.date;
      // Always reconstruct as Dayjs, even if it looks like a Dayjs object
      dateValue = dayjs(
        typeof dateValue === 'object' && dateValue !== null && dateValue.$d
          ? dateValue.$d
          : dateValue
      );
      return {
        ...updateRecipe,
        date: dateValue,
        pet: pet
      };
    }
    return {
      date: dayjs(),
      treatment: "",
      notes: "",
      items: [],
      pet: pet,
    };
  });

  const [errors, setErrors] = useState({});
  const [refs, setRefs] = useState({
    treatment: useRef(null),
    product: useRef(null),
    dosage: useRef(null),
    every_hours: useRef(null),
    total_days: useRef(null),
  });

  const [treatments, setTreatments] = useState([]);
  const [products, setProducts] = useState([]);

  const [recipeItem, setRecipeItem] = useState({ product: null, dosage: "", every_hours: "", total_days: "" });

  useEffect(() => {
    setIsLoading(true);

    updateTitle(
      pet.thumb_url,
      pet.name,
      recipe.id ? t('edit.editRecipe') : t('edit.newRecipe'),
      false,
      PetsIcon
    );

    toggleFab(false);

    const fetchData = async () => {
      try {
        // Fetch treatments and products
        const treatmentsData = await fetchForInput("pets_recipes", "pets_recipes_for_input");
        setTreatments(treatmentsData.treatments || []);
        setProducts(treatmentsData.products || []);

        setIsLoading(false);

        // Set focus on description field
        if (refs.treatment.current) {
          refs.treatment.current.focus();
        }
      } catch (error) {
        handleError(error, snackbar);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Restaurar borrador si volvemos desde SearchTreatment
  useEffect(() => {
    if (from === '/main/edit-recipe' && navState) {
      if (navState.recipe) {
        // Aseguramos que la fecha siga siendo un objeto Dayjs
        setRecipe((prev) => ({
          ...prev,
          ...navState.recipe,
          date: dayjs(navState.recipe.date)
        }));
      }
      if (navState.recipeItem) {
        setRecipeItem(navState.recipeItem);
      }
    }
  }, [from, navState]);

  // Handle treatment returned from search
  useEffect(() => {
    const treatment = location.state?.treatment;
    if (treatment) {
      setRecipe(prev => ({ ...prev, treatment }));
      // Add to treatments list if not already present
      setTreatments(prev => {
        if (!prev.find(t => t.id === treatment.id)) {
          return [...prev, treatment];
        }
        return prev;
      });
    }
  }, [location.state?.treatment]);

  const handleSearchTreatment = () => {
    // Guardar borrador antes de navegar a búsqueda de tratamientos
    setNavigation('/main/edit-recipe', {
      recipe: {
        ...recipe,
        date: dayjs(recipe.date).toDate(),
      },
      recipeItem,
      pet
    });

    navigate('/main/search-treatment', { 
      state: { 
        from: '/main/edit-recipe',
        pet
      } 
    });
  };

  // Add product to recipe
  const handleAddProduct = () => {
    if (!recipeItem.product || !recipeItem.dosage || !recipeItem.every_hours) {
      snackbar(t('edit.selectProductDose'));
      return;
    }

    if (
      !validateDecimal("every_hours", recipeItem.every_hours, setErrors, refs.every_hours) ||
      (recipeItem.total_days && !validateDecimal("total_days", recipeItem.total_days, setErrors, refs.total_days))
    )
      return;

    setRecipe({
      ...recipe,
      items: [...recipe.items, { ...recipeItem }]
    });
    setRecipeItem({ product: null, dosage: "", every_hours: "", total_days: "" });
    refs.product.current.focus();
  };

  // Remove product from recipe
  const removeItem = (index) => {
    setRecipe(prevRecipe => ({
      ...prevRecipe,
      items: prevRecipe.items.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!recipe.items || recipe.items.length === 0) {
      snackbar(t('edit.addAtLeastOneProduct'));
      return;
    }

    save();
  };

  const save = async () => {
    setIsLoading(true);
    try {
      // Format the date to MySQL datetime format before sending
      const recipeToSend = {
        ...recipe,
        date: dayjs(recipe.date).format('YYYY-MM-DD HH:mm:ss'),
        items: recipe.items.map(item => ({
          ...item,
          total_days: item.total_days || null
        }))
      };
      
      //console.log(recipeToSend);
      //return;

      let response;
      if (recipe.id) {
        response = await axios.put(
          `${import.meta.env.VITE_API_URL}pets_recipes?id=${recipe.id}`,
          recipeToSend,
          { withCredentials: true }
        );
      } else {
        response = await axios.post(
          `${import.meta.env.VITE_API_URL}pets_recipes`,
          recipeToSend,
          { withCredentials: true }
        );
      }

      //navigate(-1);
      navigate('/main/view-pet', { state: { id: recipe?.pet?.id, updateLastView: false, ...navState?.state } }, { replace: true });
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
      resetNavigation();
    }
  };

  // if (isLoading) return (
  //   <>
  //     <CircularProgress
  //       size={42}
  //       sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-21px', marginLeft: '-21px' }}
  //     />
  //   </>
  // );

  return (
    <EditPage 
      onSubmit={handleSubmit}
      isLoading={isLoading || isLoadingConfig}
      >
      <Box sx={{ width: "100%", marginTop: 1 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsPickerLocale}>
          <DateTimePicker
            label={t('common.dateTime')}
            value={recipe.date}
            onChange={(newValue) => setRecipe({ ...recipe, date: newValue })}
            renderInput={(params) => <TextField {...params} fullWidth />}
            slotProps={{ textField: { fullWidth: true } }}
            format={dateTimePickerFormat}
            ampm={useHour12}
          />
        </LocalizationProvider>
      </Box>

      <Box display="flex" gap={1} alignItems="center">
        {/* Treatment autocomplete with search functionality */}
        <Autocomplete
          fullWidth
          options={treatments}
          getOptionLabel={(option) => (option ? option.name : "")}
          value={recipe.treatment}
          onChange={(e, newValue) => { setRecipe({ ...recipe, treatment: newValue }); }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('common.treatment')}
              name="treatment"
              margin="normal"
              inputRef={refs.treatment}
              error={!!errors.treatment}
              helperText={errors.treatment}
            />
          )}
        />

        {/* Treatment search button */}
        <IconButton onClick={handleSearchTreatment} sx={{ marginTop: 1 }}>
          <SearchIcon />
        </IconButton>
      </Box>

      <TextField
        fullWidth
        margin="normal"
        label={t('common.notes')}
        name="notes"
        value={recipe.notes}
        onChange={(e) => setRecipe({ ...recipe, notes: e.target.value })}
        multiline
        rows={3}
      />

      <Divider sx={{ marginY: 2 }} />

      <Typography variant="caption">{t('common.productsServices')}</Typography>

      <Box display="flex" gap={1} alignItems="center">
        {/* Product autocomplete with search functionality */}
        <Autocomplete
          fullWidth
          options={products}
          getOptionLabel={(option) => (option ? option.name : "")}
          value={recipeItem.product}
          onChange={(e, newValue) => {
            setRecipeItem({ ...recipeItem, product: newValue });
            if (newValue) {
              refs.dosage.current.focus();
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('common.product')}
              name="product"
              margin="normal"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  refs.dosage.current.select();
                  refs.dosage.current.focus();
                }
              }}
              inputRef={refs.product}
            />
          )}
        />

        {/* Product search button */}
        {/* <IconButton onClick={handleSearchProduct} sx={{ marginTop: 1 }}>
          <SearchIcon />
        </IconButton> */}

        {/* Dosage input field */}
        <TextField
          label={t('edit.dose')}
          type="text"
          value={recipeItem.dosage}
          onChange={(e) => setRecipeItem({ ...recipeItem, dosage: e.target.value })}
          sx={{ marginTop: 1 }}
          inputRef={refs.dosage}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              refs.every_hours.current.select();
              refs.every_hours.current.focus();
            }
          }}
        />

        {/* Hours input field */}
        <TextField
          label={t('edit.everyHours')}
          type="text"
          inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
          value={recipeItem.every_hours}
          onChange={(e) => {
            const value = e.target.value.replace(/[^0-9.,]/g, "");
            setRecipeItem({ ...recipeItem, every_hours: value });
          }}
          sx={{ marginTop: 1, "& .MuiInputBase-input": { textAlign: "right" } }}
          inputRef={refs.every_hours}
          error={!!errors.every_hours}
          helperText={errors.every_hours}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              refs.total_days.current.select();
              refs.total_days.current.focus();
            }
          }}
        />

        {/* Days input field */}
        <TextField
          label={t('edit.totalDays')}
          type="text"
          inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
          value={recipeItem.total_days}
          onChange={(e) => {
            const value = e.target.value.replace(/[^0-9.,]/g, "");
            setRecipeItem({ ...recipeItem, total_days: value });
          }}
          sx={{ marginTop: 1, "& .MuiInputBase-input": { textAlign: "right" } }}
          inputRef={refs.total_days}
          error={!!errors.total_days}
          helperText={errors.total_days}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAddProduct();
              refs.product.current.select();
              refs.product.current.focus();
            }
          }}
        />

        <IconButton onClick={handleAddProduct} sx={{ marginTop: 1 }}>
          <KeyboardReturnIcon />
        </IconButton>
      </Box>

      {/* Product list */}  
      <Box>
        {recipe.items?.map((item, index) => (
          <ListItem
            key={index}
            secondaryAction={
              <IconButton
                edge="end"
                onClick={() => removeItem(index)}
                sx={{ width: "42px", height: "42px" }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
            sx={{ paddingRight: "56px" }}
          >
            <ListItemText
              primary={
                <Box display="flex" alignItems="center">
                  <InventoryIcon sx={{ fontSize: 20, mr: 1 }} />
                  {item.product.name}
                </Box>
              }
              secondary={`${t('edit.dose')}: ${item.dosage} | ${t('edit.everyHours')}: ${item.every_hours}${item.total_days ? ` | ${t('edit.totalDays')}: ${item.total_days}` : ''}`}
            />
          </ListItem>
        ))}
      </Box>
    </EditPage>
  );
};

export default EditRecipe;
